// src/screens/MarketplaceScreen.js - REVISED WITH AUTH GUARD CLAUSES AND FETCH FIXES
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getGeoFromCEP } from '../utils/helpers';
import { collection, query, where, orderBy, limit, getDocs, startAfter, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { db, callable } from '../firebase';
import { useNavigate, useNavigationType } from 'react-router-dom';
import AdSlot from '../components/AdSlot';

const LoadingComponent = () => (
    <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">Carregando marketplace...</div>
    </div>
);

export default function MarketplaceScreen({ setModal }) {
    const { user, profile, loading: authLoading, validationStatus } = useAuth();
    const navigate = useNavigate();
    // Back from an item restores the list position (the list reloads on
    // every visit, so the browser can't restore it by itself).
    const navigationType = useNavigationType();
    const scrollRestored = useRef(false);
    useEffect(() => () => {
        try { sessionStorage.setItem('market_scroll', String(window.scrollY)); } catch (e) { /* private mode */ }
    }, []);

    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    // Randomize - Add shuffledItems state
    const [shuffledItems, setShuffledItems] = useState([]);    
    const [categories, setCategories] = useState([]);
    const [maxRadius, setMaxRadius] = useState(200);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [searchCep, setSearchCep] = useState('');
    // Infinity = no distance limit (the slider's last notch). Any finite value
    // is applied as a real radius filter.
    const [searchRadius, setSearchRadius] = useState(Infinity);
    const [sortOption, setSortOption] = useState('createdAt_desc');
    
    const [cepCache, setCepCache] = useState({});

    const [activeFilters, setActiveFilters] = useState({
        category: '',
        location: null,
        sort: 'createdAt_desc'
    });
    // - Add the shuffle function
    const shuffleArray = (arr) => {
      const newArr = [...arr];
      for (let i = newArr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
      }
      return newArr;
    };

    const ITEMS_PER_PAGE = 20;

    // Main data fetching function
    const fetchItems = useCallback(async (loadMore = false) => {
        // Guard against fetching without a valid user.
        if (!user?.uid) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        if (!loadMore) {
            setItems([]);
            setLastVisible(null);
            setHasMore(true);
        }

        try {
            let itemsFromQuery = [];
            
            if (activeFilters.location) {
                const searchNearbyItems = callable('searchNearbyItems');
                
                const result = await searchNearbyItems({
                    center: activeFilters.location.center,
                    radius: activeFilters.location.radius,
                    category: activeFilters.category || null
                });
                
                itemsFromQuery = result.data.items;
                setHasMore(false);

                if (itemsFromQuery.length === 0) {
                    setModal({ message: "Nenhum item encontrado. Mostrando itens de outras localidades." });
                    setActiveFilters(prev => ({ ...prev, location: null }));
                    return;
                }
            } else {
                let baseQuery = query(collection(db, "items"), where('status', '==', 'available'));
                
                if (activeFilters.category) {
                    baseQuery = query(baseQuery, where('category', '==', activeFilters.category));
                }

                const [field, direction] = sortOption.split('_');
                baseQuery = query(baseQuery, orderBy(field, direction));

                if (loadMore && lastVisible) {
                    baseQuery = query(baseQuery, startAfter(lastVisible));
                }
                baseQuery = query(baseQuery, limit(ITEMS_PER_PAGE));

                const documentSnapshots = await getDocs(baseQuery);
                itemsFromQuery = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                const newLastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
                setLastVisible(newLastVisible);
                setHasMore(documentSnapshots.docs.length === ITEMS_PER_PAGE);
            }

            // ✅ FIX #2: Removed the line that filtered out the user's own items.
           //  const filteredItems = itemsFromQuery.filter(item => item.ownerId !== user.uid);

            if (loadMore) {
                // Now uses itemsFromQuery directly
              setItems(prevItems => [...prevItems, ...itemsFromQuery]);
             //     setItems(prevItems => [...prevItems, ...filteredItems]);
            } else {
                // Now uses itemsFromQuery directly
                setItems(itemsFromQuery);
               //    setItems(filteredItems);

            }

        } catch (error) {
            console.error("Error fetching items:", error);
            const errorMessage = error.message || "Ocorreu um erro ao buscar os itens.";
            setModal({ message: errorMessage });
        } finally {
            setIsLoading(false);
        }
    }, [activeFilters, lastVisible, setModal, sortOption, user?.uid]);


    // Effect to fetch initial items and settings AFTER auth is confirmed
    useEffect(() => {
        // ✅ FIX #1: This guard ensures that the initial fetch only runs
        // once the profile is fully loaded and available.
        if (!profile?.id) {
            return;
        }

        fetchItems(false);

        const settingsDocRef = doc(db, "app_settings", "marketplace");
        const unsubSettings = onSnapshot(settingsDocRef, (doc) => {
            if (doc.exists() && doc.data().maxRadius) {
                const newMaxRadius = doc.data().maxRadius;
                setMaxRadius(newMaxRadius);
            }
        });

        const categoriesColRef = collection(db, "categories");
        const unsubCategories = onSnapshot(query(categoriesColRef, orderBy("name")), (snapshot) => {
            const cats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCategories(cats);
        });

        return () => {
            unsubSettings();
            unsubCategories();
        };
        // ✅ FIX #1: The dependency array now includes profile.id.
        // This makes the effect re-run when the profile is first loaded,
        // triggering the initial item fetch correctly.
    }, [activeFilters, profile?.id]);
    
    // - Shuffle items whenever items changes
    useEffect(() => {
      if (items.length === 0) return;

      const savedOrder = sessionStorage.getItem("market_shuffle_order");

      if (savedOrder) {
        // Reuse the same order
        const order = JSON.parse(savedOrder);
        const ordered = order
          .map(id => items.find(item => item.id === id))
          .filter(Boolean);

        // Add any new items that weren't in the saved order
        const missing = items.filter(item => !order.includes(item.id));

        setShuffledItems([...ordered, ...missing]);
      } else {
        // First time this session → shuffle and save
        const shuffled = shuffleArray(items);
        setShuffledItems(shuffled);
        sessionStorage.setItem(
          "market_shuffle_order",
          JSON.stringify(shuffled.map(item => item.id))
        );
      }
}, [items]);
 	
    useEffect(() => {
        if (scrollRestored.current || navigationType !== 'POP' || shuffledItems.length === 0) return;
        scrollRestored.current = true;
        let y = 0;
        try { y = Number(sessionStorage.getItem('market_scroll')) || 0; } catch (e) { /* private mode */ }
        if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y));
    }, [navigationType, shuffledItems.length]);

    const handleApplyFilters = async () => {
        const cepValue = searchCep.trim();
        const radiusChanged = Number.isFinite(searchRadius);
        const locationSearchIsActive = cepValue || radiusChanged;
        
        let locationFilter = null;
        let searchLocation = null;
        let cepForDisplay = '';

        const getAndCacheGeo = async (cepToFetch) => {
            if (cepCache[cepToFetch]) {
                return cepCache[cepToFetch];
            }
            const location = await getGeoFromCEP(cepToFetch);
            if (location) {
                setCepCache(prev => ({...prev, [cepToFetch]: location}));
            }
            return location;
        };

        if (locationSearchIsActive) {
            if (cepValue) {
                searchLocation = await getAndCacheGeo(cepValue);
                cepForDisplay = cepValue;
                if (!searchLocation) {
                    setModal({ message: `O CEP "${cepValue}" parece ser inválido.` });
                    return;
                }
            } else if (profile?.location?.lat && profile?.location?.lon) {
                searchLocation = { lat: profile.location.lat, lon: profile.location.lon };
                cepForDisplay = profile.cep || 'Sua Localização';
            } else if (profile?.cep) {
                searchLocation = await getAndCacheGeo(profile.cep);
                cepForDisplay = profile.cep;
                if (!searchLocation) {
                    setModal({ message: `Seu CEP de perfil "${profile.cep}" parece ser inválido.` });
                    return;
                }
            } else {
                searchLocation = await getAndCacheGeo('01311-000'); // Fallback
                cepForDisplay = '01311-000';
                setModal({ message: "Nenhuma localização encontrada. Usando localização padrão (São Paulo)." });
            }

            if (searchLocation) {
                locationFilter = {
                    cep: cepForDisplay,
                    radius: Number.isFinite(searchRadius) ? Math.min(searchRadius, maxRadius) : maxRadius,
                    center: [searchLocation.lat, searchLocation.lon]
                };
            }
        }
        
        setActiveFilters({
            category: selectedCategory,
            sort: sortOption,
            location: locationFilter
        });
    };

    const clearFilters = () => {
        setSelectedCategory('');
        setSearchCep('');
        setSearchRadius(Infinity);
        setSortOption('createdAt_desc');
        setActiveFilters({
            category: '',
            location: null,
            sort: 'createdAt_desc'
        });
    };
    
    const handleLoadMore = () => {
        if (!isLoading && hasMore) {
            fetchItems(true);
        }
    };

    // This robust guard clause remains essential.
    if (authLoading || validationStatus !== 'success' || !user || !profile) {
        return <LoadingComponent />;
    }

    return (
        <>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Escambo Center</h1>
            
            <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                    {/* Category Dropdown */}
                    <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">Categoria</label>
                        <select id="category" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md">
                            <option value="">Todas</option>
                            {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                        </select>
                    </div>
                    {/* Sort Dropdown */}
                    <div>
                        <label htmlFor="sort" className="block text-sm font-medium text-gray-700">Ordenar por</label>
                        <select id="sort" value={sortOption} onChange={(e) => setSortOption(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md">
                            <option value="createdAt_desc">Mais Recentes</option>
                            <option value="createdAt_asc">Mais Antigos</option>
                            <option value="credits_asc">Créditos (Menor)</option>
                            <option value="credits_desc">Créditos (Maior)</option>
                        </select>
                    </div>
                    {/* CEP Input */}
                    <div>
                        <label htmlFor="cep" className="block text-sm font-medium text-gray-700">CEP para busca específica</label>
                        <input id="cep" type="text" value={searchCep} onChange={(e) => setSearchCep(e.target.value)} placeholder="Digite um CEP para buscar" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" />
                    </div>
                </div>
                {/* Radius Slider */}
                <div className="mt-4">
                    <label htmlFor="radius" className="block text-sm font-medium text-gray-700">Raio da busca: {Number.isFinite(searchRadius) ? `${searchRadius} km` : 'sem limite (todo o Brasil)'}</label>
                    {/* Last notch (maxRadius + 5) = no limit; it must not pretend to be a radius. */}
                    <input id="radius" type="range" min="5" max={maxRadius + 5} step="5" value={Number.isFinite(searchRadius) ? Math.min(searchRadius, maxRadius) : maxRadius + 5} onChange={e => { const v = parseInt(e.target.value, 10); setSearchRadius(v > maxRadius ? Infinity : v); }} aria-valuetext={Number.isFinite(searchRadius) ? `${searchRadius} km` : 'sem limite'} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer" />
                </div>
                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <button onClick={handleApplyFilters} disabled={isLoading} className="bg-green-600 text-white px-6 py-2 rounded-lg shadow hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-wait flex-shrink-0">
                        {isLoading ? "Buscando..." : "Aplicar Filtros"}
                    </button>
                    {(activeFilters.category || activeFilters.location || activeFilters.sort !== 'createdAt_desc') && (
                        <button onClick={clearFilters} className="text-sm text-blue-600 hover:underline">Limpar filtros</button>
                    )}
                </div>
            </div>

            {/* Platform ad (admin-controlled) */}
            <AdSlot placement="marketplace_inline" className="mb-6" />

            {/* Display Items Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {shuffledItems.map(item => (
               /*    <div key={item.id} onClick={() => { if (item.ownerId !== profile?.id) { navigate(`/market/${item.id}`); } }} className="border rounded-lg overflow-hidden shadow-sm bg-white transition-transform transform hover:-translate-y-1 relative cursor-pointer">
                        <img src={(item.imageUrls && item.imageUrls[0]) || 'https://placehold.co/300x300/e2e8f0/64748b?text=Item'} alt={item.title} className="w-full h-40 object-cover" /> */
	            <div key={item.id} onClick={() => { navigate(`/market/${item.id}`); }} className="border rounded-lg overflow-hidden shadow-sm bg-white transition-transform transform hover:-translate-y-1 relative cursor-pointer">
                        {/* 480 px thumbnail (functions/lib/thumbnails.js), full photo as fallback */}
                        <img src={item.thumbUrls?.[0] || item.imageUrls?.[0] || 'https://placehold.co/300x300/e2e8f0/64748b?text=Item'} alt={item.title} className="w-full h-40 object-cover" loading="lazy" decoding="async" width="300" height="160" />
                        <div className="p-3">
                            <h3 className="font-semibold text-gray-800 truncate">{item.title}</h3>
                            <p className="text-green-600 font-bold">{item.credits} C</p>
                        </div>
                        {item.ownerId === profile?.id && (<div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center pointer-events-none"><p className="text-white font-bold text-lg">Seu Item</p></div>)}
                    </div>
                ))}
            </div>
            
            {/* Loading/Empty/Load More States */}
            {isLoading && items.length === 0 && <div className="mt-8 text-center"><p className="text-gray-500">Buscando itens...</p></div>}
            {!isLoading && items.length === 0 && <div className="mt-8 text-center"><p className="text-gray-500">Nenhum item encontrado.</p></div>}
            {hasMore && !isLoading && !activeFilters.location && (
                <div className="mt-8 text-center">
                    <button onClick={handleLoadMore} className="bg-blue-500 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-600 transition">
                        Carregar Mais
                    </button>
                </div>
            )}
        </>
    );
}

